import { ProfileCompletionFields } from 'src/api/profile-completion';
import {
  AuditableEntity,
  UserIdentity,
  FullName,
  PhoneNumber,
  UserIdentityDto,
  RichText,
  LocationInfo,
  FileData,
  MoneyTransactionStatus,
  AccountCancellationRequestStatus,
  ContactAgentAllowedTargets,
  ProfileImage
} from '../common';
import { ByLocationWorkTypePreference, Experience, PersonProfile } from '../person-profile/types';
import { IndustryArea } from '../taxonomies/industry-areas-api';
import { JobRole } from '../taxonomies/job-roles-api';
import { JobSkill } from '../taxonomies/job-skills-api';
import { t } from 'i18next';
import { MatchMeetingRejectionReason, MatchStatusGroup } from '../match/types';

/**
 * Los campos de completitud (issue #1774) los aporta `ProfileCompletionFields`. Van en el usuario y no en el
 * perfil porque el 10% del email verificado es el del propio usuario.
 */
export interface EnterpriseUser extends AuditableEntity<string>, ProfileCompletionFields {
  enterpriseProfileId: string;
  enterpriseProfile: EnterpriseProfile;
  userIdentityId: string;
  userIdentity: UserIdentity;
  fullName: FullName;
  phoneNumber: PhoneNumber;
  profileImageFileId?: string;
  isAdmin: boolean;
  registrationCompleted: boolean;
  active: boolean;
  receiveEmailNotifications: boolean;
  rejectedCancellationRequestComment: string;
  cancellationRequestStatus: AccountCancellationRequestStatus;
  contactAgentId: string;
  contactAgent: ContactAgent;
  onboardingCompleted: boolean;
}

export type DateRangeType = 'Undefined' | 'Concrete' | 'NextMonths';

export interface AsNeeded {
  asNeeded: boolean;
  dateRangeType: DateRangeType;
}
export interface ContactAgent {
  id: string;
  email: string;
  phoneNumber: PhoneNumber;
  fullName: FullName;
  personUsers: PersonProfile[];
  enterpriseUsers: EnterpriseUser[];
  bannerImageFileId: string;
  bannerImageFile: FileData;
  meetingSchedulingUrl: string;
  allowedTargets: ContactAgentAllowedTargets;
}

export enum EnterpriseSize {
  Microenterprise = 'Microenterprise', // 1 a 10
  SmallCompany = 'SmallCompany', //11 a 50
  MediumCompany = 'MediumCompany', //51 a 250
  BigCompany = 'BigCompany' //+250
}

export const EnterpriseSizeT = {
  [EnterpriseSize.Microenterprise]: t('Microempresa'),
  [EnterpriseSize.SmallCompany]: t('Pequeña empresa'),
  [EnterpriseSize.MediumCompany]: t('Mediana empresa'),
  [EnterpriseSize.BigCompany]: t('Gran empresa')
};

export const EnterpriseSizeTFull = {
  [EnterpriseSize.Microenterprise]: t('Microempresa (1-10 empleados)'),
  [EnterpriseSize.SmallCompany]: t('Pequeña empresa (11-50 empleados)'),
  [EnterpriseSize.MediumCompany]: t('Mediana empresa (51-250 empleados)'),
  [EnterpriseSize.BigCompany]: t('Gran empresa (+250 empleados)')
};

export const EnterpriseSizeTMid = {
  [EnterpriseSize.Microenterprise]: t('1-10 empleados'),
  [EnterpriseSize.SmallCompany]: t('11-50 empleados'),
  [EnterpriseSize.MediumCompany]: t('51-250 empleados'),
  [EnterpriseSize.BigCompany]: t('+250 empleados')
};

export enum EnterpriseCancelAccountReason {
  DidntFindSuitableCandidates = 0,
  NeverReachedAgreements = 10,
  WasRejectedTooManyTimes = 20,
  TooManyNotifications = 30,
  DontSeeTheAdvantageOfUsingJobs2Live = 40,
  FoundBetterAlternatives = 50,
  OtherReason = 60
}

export interface EnterpriseProfile extends AuditableEntity<string> {
  name: string;
  profileImageFileId?: string;
  websiteUrl: string;
  industryAreaId?: number;
  industryArea: IndustryArea;
  description: string;
  enterpriseSize?: EnterpriseSize;
  adminUserId: string;
  enterpriseUsers: EnterpriseUser[];
  bannerImageFileId?: string;
  ratingAverages: EnterpriseRatingAverage;
  projects: Project[];
  linkedInUrl: string;
  originDraftShortlistId?: string;
  onboardingDraftJson?: string;
}

export interface EnterpriseRatingAverage {
  total: number;
  workClimateRating: number;
  organizationRating: number;
  valuesRating: number;
}

/**
 * El autor de una valoración de empresa, tal y como lo sirve el endpoint: un `ReviewAuthorDto`, no un perfil
 * entero. Lo privado no está aquí porque no está en el modelo, que es lo que cerró el #1919 y completa el
 * #1923. Pedir por OData un campo que no esté en esta lista no devuelve el dato: devuelve un error.
 */
export interface EnterpriseReviewAuthor {
  id: string;
  nickName?: string;
  publicFullName?: FullName;
  profileImage?: ProfileImage;
}

/**
 * Sin los cuatro campos del rastro de auditoría que hereda de `AuditableEntity`, y por eso con `Omit`: desde el
 * #1923 el servidor rechaza cualquier consulta de valoraciones que los nombre, así que tenerlos en el tipo sólo
 * servía para que una pantalla los pidiera y se encontrara el rechazo en tiempo de ejecución. Quitándolos de
 * aquí, `keyof EnterpriseReview` ya no los tiene y el error sale al compilar, en ésta y en la siguiente pantalla
 * que liste valoraciones. Nadie los leía: `createdById` es el `UserIdentityId` de quien escribió la valoración,
 * que es justo el dato que el #1923 esconde. `createdOn` y `modifiedOn` sí siguen, que son los que se pintan.
 */
export interface EnterpriseReview extends Omit<
  AuditableEntity<string>,
  'createdById' | 'createdByDisplayName' | 'modifiedById' | 'modifiedByDisplayName'
> {
  id: string;
  workClimateRating: number;
  organizationRating: number;
  valuesRating: number;
  comment: string | undefined;
  verifiedComment: boolean;
  /** El `uint Timestamp` del DTO, que el listado pide y nadie lee todavía. */
  timestamp: number;
  personProfile?: EnterpriseReviewAuthor;
}

export interface UpsertEnterpriseBasicInfoCommand {
  adminFirstName: string;
  adminLastName: string;
  phoneNumber: string;
  phoneNumberCountryId: number;
  websiteUrl: string;
  enterpriseName: string;
}

export interface UpdateEnterpriseUserProfileInfoBody {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  phoneNumberCountryId?: number;
  email?: string;
}

export interface UpdateEnterpriseProfileInfoBody {
  name: string;
  websiteUrl?: string;
  industryAreaId?: number;
  description?: string;
  enterpriseSize?: EnterpriseSize;
  descriptionChanged?: boolean;
  enterpriseSizeChanged?: boolean;
  websiteUrlChanged?: boolean;
  industryAreaIdChanged?: boolean;
}

export interface AddEnterpriseUserCommandBody {
  userIdentityId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface EnterpriseUserDto {
  id: string;
  enterpriseProfileId: string;
  userIdentityId: string;
  userIdentity: UserIdentityDto;
  fullName: FullName;
  phoneNumber: PhoneNumber;
  profileImageFileId?: string;
  isAdmin: boolean;
  registrationCompleted: boolean;
  active: boolean;
}

export interface Project extends AuditableEntity<string> {
  enterpriseProfileId: string;
  enterpriseProfile: EnterpriseProfile;
  enterpriseUserId: string;
  enterpriseUser: EnterpriseUser;
  name: string;
  objectives: RichText;
  projectManagerName: string;
  startDate: string;
  sustainableDevelopmentGoals: SustainableDevelopmentGoals[];
  serviceRequests: ServiceRequest[];
}

export interface ServiceRequest extends AuditableEntity<string> {
  projectId: string;
  project: Project;
  roleId: string;
  role: JobRole;
  // Always present on a published request (the API validates it). A draft autofilled from a LinkedIn offer that
  // states no experience requirement arrives without it until the enterprise fills the form, so the screens that
  // list or prefill drafts do check it before reading it.
  roleExperience: Experience;
  skills: ServiceRequestJobRoleSkill[];
  location: LocationInfo;
  locationWorkPreference: ByLocationWorkTypePreference;
  tentativeStartDate: string;
  tentativeEndDate: string;
  serviceDurationInMonths?: number;
  description: RichText;
  isDraft: boolean;
  notificationsFrequency: ServiceRequestNotificationFrequency;
  isActive: boolean;
  publishDate?: string;
  acceptedMatchesCount: number;
  matchMeetingsCount: number;
  newMatchesAddedOn?: string;
  newMatchesCount: number;
  rejectedMatchesCount: number;
  selectedMatchesCount: number;
  contractsCount: number;
  state: ServiceRequestState;
  doNotAskDiscardServiceComplete: boolean;
  doNotAskDiscardBlockBeforeMeeting: boolean;
}

export type ServiceRequestSkillDto = {
  id?: string;
  name: string;
  importance: ServiceRequestSkillImportance;
  order?: number;
};

export enum ServiceRequestSkillImportance {
  None = 'None',
  NiceToHave = 'NiceToHave',
  MustHave = 'MustHave'
}

export interface ServiceRequestJobRoleSkill extends AuditableEntity<string> {
  serviceRequestId: string;
  roleId: string;
  skillId: string;
  skill: JobSkill;
  importance: ServiceRequestSkillImportance;
  order: number;
}

export enum ServiceRequestNotificationFrequency {
  Never = 'Never',
  OnceADay = 'OnceADay',
  OnEveryMatch = 'OnEveryMatch',
  OnceAWeek = 'OnceAWeek'
}

export enum SustainableDevelopmentGoals {
  NoPoverty = 'NoPoverty',
  ZeroHunger = 'ZeroHunger',
  GoodHealthAndWellBeing = 'GoodHealthAndWellBeing',
  QualityEducation = 'QualityEducation',
  GenderEquality = 'GenderEquality',
  CleanWaterAndSanitation = 'CleanWaterAndSanitation',
  AffordableAndCleanEnergy = 'AffordableAndCleanEnergy',
  DecentWorkAndEconomicGrowth = 'DecentWorkAndEconomicGrowth',
  IndustryInnovationAndInfrastructure = 'IndustryInnovationAndInfrastructure',
  ReducedInequalities = 'ReducedInequalities',
  SustainableCitiesAndCommunities = 'SustainableCitiesAndCommunities',
  ResponsibleConsumptionAndProduction = 'ResponsibleConsumptionAndProduction',
  ClimateAction = 'ClimateAction',
  LifeBelowWater = 'LifeBelowWater',
  LifeOnLand = 'LifeOnLand',
  PeaceJusticeAndStrongInstitutions = 'PeaceJusticeAndStrongInstitutions',
  Partnerships = 'Partnerships'
}

export interface CreateProjectDto {
  name: string;
  objectives: RichText;
  projectManagerName: string;
  startDate?: string;
  sustainableDevelopmentGoals: SustainableDevelopmentGoals[];
}
export interface CreateServiceRequestDto {
  roleId: string;
  // Only optional for drafts (isDraft: true); a request that is published needs it
  roleExperience?: Experience;
  skills: SkillDto[];
  location: LocationInfo;
  locationWorkPreference: ByLocationWorkTypePreference;
  // Omit to let the server start the service today (in Spain time)
  tentativeStartDate?: string;
  serviceDurationInMonths?: number;
  tentativeEndDate?: string;
  description: RichText;
  isDraft: boolean;
  notificationsFrequency: ServiceRequestNotificationFrequency;
  isActive?: boolean;
  state?: ServiceRequestState;
}

export interface SkillDto {
  id: string;
  name: string;
}

export interface UpdateServiceRequestDto {
  roleId: string;
  roleExperience: Experience;
  skills: SkillDto[];
  location: LocationInfo;
  locationWorkPreference: ByLocationWorkTypePreference;
  // Omit to keep the start date the request already has
  tentativeStartDate?: string;
  // Unlike tentativeStartDate, these two are not preserved: omitting them clears the stored values, because that is
  // how the "a convenir" duration is expressed. Echo the current values back unless you mean to erase them.
  serviceDurationInMonths?: number;
  tentativeEndDate?: string;
  description: RichText;
  isDraft: boolean;
  notificationsFrequency: ServiceRequestNotificationFrequency;
}

export interface CreateProjectWithRequestsBody {
  projectInfo?: CreateProjectDto;
  projectId?: string;
  requests: CreateServiceRequestDto[];
}

export interface UpdateProjectBody {
  name: string;
  objectives: RichText;
  projectManagerName: string;
  startDate?: string;
  sustainableDevelopmentGoals: SustainableDevelopmentGoals[];
}

export interface UpsertProjectBody {
  projectId?: string;
  name?: string;
  objectives?: RichText;
  projectManagerName?: string;
  startDate?: string;
  sustainableDevelopmentGoals?: SustainableDevelopmentGoals[];
}

export interface DiscardCandidateForServiceRequestBody {
  reason: MatchMeetingRejectionReason;
  reasonText?: string;
  doNotAskAgain?: boolean;
  statusGroup?: MatchStatusGroup;
}

export interface Bill extends AuditableEntity<string> {
  date: string;
  amount: number;
  number: number;
  status: MoneyTransactionStatus;
  modifiedOn: string;
  statusMessage: string;
  file?: FileData;
}

export enum ServiceRequestCloseReason {
  CandidateHired = 'CandidateHired',
  InternalReasons = 'InternalReasons'
}

export interface ServiceRequestCloseOpenOffer {
  contractId: string;
  candidateName: string;
}

export interface ServiceRequestClosePreview {
  waitingCandidates: number;
  openOffers: ServiceRequestCloseOpenOffer[];
}

export interface CloseServiceRequestBody {
  reason: ServiceRequestCloseReason;
  confirmCancelOffers: boolean;
}

export enum ServiceRequestState {
  NotReviewed = 'NotReviewed',
  Open = 'Open',
  OnGoing = 'OnGoing',
  Finished = 'Finished'
}

export interface EnterpriseAutocompletedProfilePatchDto {
  // Enterprise
  name?: string;
  description?: string;
  websiteUrl?: string;
  enterpriseSize?: EnterpriseSize;
  industryAreaId?: number;
  profileImageFileId?: string;
  bannerImageFileId?: string;

  // Admin
  adminName?: FullName;
  adminProfileImageFileId?: string;
  adminEmail?: string;
  adminPhoneNumber?: PhoneNumber;

  // Project
  projectName?: string;
  projectObjectives?: RichText;
  projectManagerName?: string;

  // ServiceRequest
  serviceRequestRole?: JobRole;
  serviceRequestRoleExperience?: Experience;
  serviceRequestSkills?: JobSkill[];
  serviceRequestLocation?: LocationInfo;
  serviceRequestLocationWorkPreference?: ByLocationWorkTypePreference;
}

export enum DraftShortlistItemStatus {
  Created = 'Created',
  Failed = 'Failed'
}

export interface DraftShortlistFromLinkedinItemResultDto {
  linkedInPostingId: number;
  status: DraftShortlistItemStatus;
  error?: string;
  draftShortlistId?: string;
  draftShortlistUrl?: string;
  draftShortlistCandidateCount: number;
}

export enum EnterpriseAutocompletedFieldId {
  // Enterprise
  Name = 'name',
  Description = 'description',
  WebsiteUrl = 'websiteUrl',
  EnterpriseSize = 'enterpriseSize',
  IndustryAreaId = 'industryAreaId',
  ProfileImageFileId = 'profileImageFileId',
  BannerImageFileId = 'bannerImageFileId',

  // Admin
  AdminName = 'adminName',
  AdminProfileImageId = 'adminProfileImageId',
  AdminEmail = 'adminEmail',
  AdminPhoneNumber = 'adminPhoneNumber',

  // Project
  ProjectName = 'projectName',
  ProjectObjectives = 'projectObjectives',
  ProjectManagerName = 'projectManagerName',

  // ServiceRequest
  ServiceRequestRole = 'serviceRequestRole',
  ServiceRequestRoleExperience = 'serviceRequestRoleExperience',
  ServiceRequestSkills = 'serviceRequestSkills',
  ServiceRequestLocation = 'serviceRequestLocation',
  ServiceRequestLocationWorkPreference = 'serviceRequestLocationWorkPreference',
  ServiceRequestDescription = 'serviceRequestDescription'
}

export type EnterpriseAutocompletedField =
  | { id: EnterpriseAutocompletedFieldId.Name; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.Description; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.WebsiteUrl; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.EnterpriseSize; label: string; value: EnterpriseSize }
  | { id: EnterpriseAutocompletedFieldId.IndustryAreaId; label: string; value: number }
  | { id: EnterpriseAutocompletedFieldId.ProfileImageFileId; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.BannerImageFileId; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.AdminName; label: string; value: FullName }
  | { id: EnterpriseAutocompletedFieldId.AdminProfileImageId; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.AdminEmail; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.AdminPhoneNumber; label: string; value: PhoneNumber }
  | { id: EnterpriseAutocompletedFieldId.ProjectName; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.ProjectObjectives; label: string; value: RichText }
  | { id: EnterpriseAutocompletedFieldId.ProjectManagerName; label: string; value: string }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestRole; label: string; value: JobRole }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience; label: string; value: Experience }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestSkills; label: string; value: JobSkill[] }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestLocation; label: string; value: LocationInfo }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference; label: string; value: ByLocationWorkTypePreference }
  | { id: EnterpriseAutocompletedFieldId.ServiceRequestDescription; label: string; value: RichText };

export type EnterpriseAutocompletedFlags = {
  [K in EnterpriseAutocompletedFieldId]?: boolean;
};
